import { cn } from "@/lib/utils";

/**
 * The round verdict mark: a tick, an "!" for attention, or blocked for a criterion not met. Drawn through the
 * readiness tokens so it has a dark value; `aria-label` is often the only thing on the row that states the verdict.
 */
export type GlyphStatus = "tick" | "attention" | "blocked";
const GLYPH_TONE: Record<GlyphStatus, string> = {
  tick: "bg-readiness-ready-bg text-readiness-ready-fg ring-readiness-ready-edge",
  // Solid: in the pale attention tint it all but vanished beside a value. `warning` is the same amber as a token with a dark value.
  attention: "bg-warning text-warning-foreground shadow-xs ring-2 ring-warning/20",
  // "Blocked": solid for the same reason, in the blocked family.
  blocked: "bg-readiness-blocked-fg text-readiness-blocked-bg shadow-xs",
};
export function StatusGlyph({
  status,
  "aria-label": label,
  size = "default",
  className,
}: {
  status: GlyphStatus;
  /** The accessible name. Omit it only where the verdict is said in text beside the disc. */
  "aria-label"?: string;
  /** `sm` 16px for dense lists; `default` 20px beside a field row; `lg` 44px in a dialog header. */
  size?: "sm" | "default" | "lg";
  className?: string;
}) {
  const box =
    size === "sm" ? "size-4 [&_svg]:size-2.5" : size === "lg" ? "size-11 [&_svg]:size-5" : "size-5 [&_svg]:size-3";
  return (
    <span
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full",
        status === "tick" && "ring-1 ring-inset",
        box,
        GLYPH_TONE[status],
        className
      )}
    >
      {status === "tick" ? (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden>
          <path
            d="M5 10.5l3.2 3.2L15 7"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden>
          <path d="M10 5.5v5M10 14h.01" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
        </svg>
      )}
    </span>
  );
}
